import {
  ChatManageRouteStopsAction,
  ChatRouteCardAffectedStop,
  ChatRouteCardSnapshot,
  ChatRouteCardStop,
  ChatRouteStopChangeKind,
  ChatRouteStopEntityType,
  ChatRouteStopOperation,
  ChatRouteStops,
  ChatRouteStopTarget,
} from "../../types/chat/ChatContent";

import { isRecord, ROUTE_STOP_ENTITY_TYPES } from "./chatWireGuards";
import parseSkippedRouteStops from "./parseSkippedRouteStops";

const OPERATIONS: ChatRouteStopOperation[] = ["add", "remove", "replace"];
const CHANGE_KINDS: ChatRouteStopChangeKind[] = ["added", "moved", "removed"];

const asStopEntityType = (value: unknown): ChatRouteStopEntityType | undefined =>
  ROUTE_STOP_ENTITY_TYPES.find((candidate) => candidate === value);

// An absent key and an explicit null mean the same thing to every renderer here — no value — so
// they are normalised together rather than kept apart for a distinction nothing reads.
const asNullableString = (value: unknown): null | string =>
  typeof value === "string" ? value : null;

const asNullableNumber = (value: unknown): null | number =>
  typeof value === "number" ? value : null;

// A stop is deliberately lenient: null identity is an address-only stop, a null name is a stop the
// platform could not name, and a null arrival is either an unpriced route or a visited stop. Only a
// non-object or a missing visited flag is malformed enough to reject.
const parseRouteCardStop = (value: unknown): ChatRouteCardStop | undefined =>
  isRecord(value) && typeof value.checkedOff === "boolean"
    ? {
        arrivalTime: asNullableString(value.arrivalTime),
        checkedOff: value.checkedOff,
        displayOrder: asNullableNumber(value.displayOrder),
        entityId: asNullableNumber(value.entityId),
        entityType: asStopEntityType(value.entityType) ?? null,
        name: asNullableString(value.name),
        ...(value.previousArrivalTime === undefined
          ? {}
          : { previousArrivalTime: asNullableString(value.previousArrivalTime) }),
      }
    : undefined;

// changeKind has no renderer — grep confirms the parser is its only reader — so an unrecognised
// value (the platform adding a fourth kind, say) is normalised to null rather than rejecting the
// whole snapshot, which is what returning undefined here used to do.
const parseAffectedStop = (value: unknown): ChatRouteCardAffectedStop | undefined =>
  isRecord(value)
    ? {
        changeKind: CHANGE_KINDS.find((candidate) => candidate === value.changeKind) ?? null,
        entityId: asNullableNumber(value.entityId),
        entityType: asStopEntityType(value.entityType) ?? null,
        ...(value.fromIndex === undefined ? {} : { fromIndex: asNullableNumber(value.fromIndex) }),
        ...(value.previousArrivalTime === undefined
          ? {}
          : { previousArrivalTime: asNullableString(value.previousArrivalTime) }),
        ...(value.toIndex === undefined ? {} : { toIndex: asNullableNumber(value.toIndex) }),
      }
    : undefined;

export const parseRouteStops = (value: Record<string, unknown>): ChatRouteStops | undefined => {
  if (!Array.isArray(value.stops)) {
    return undefined;
  }
  const stops = value.stops.map(parseRouteCardStop);
  return stops.every(Boolean)
    ? {
        // Not `=== true`: an absent or renamed flag would otherwise read as unpriced and suppress
        // arrival times that are actually present. Only an explicit `false` means unpriced.
        priced: value.priced !== false,
        startAt: asNullableString(value.startAt),
        stops: stops as ChatRouteCardStop[],
      }
    : undefined;
};

// An absent, empty or malformed snapshot drops the SNAPSHOT and keeps the proposal: the card falls
// back to a one-line description of the change and stays confirmable, which is the right rendering
// for a proposal the platform could not price.
export const parseRouteCardSnapshot = (value: unknown): ChatRouteCardSnapshot | undefined => {
  if (!isRecord(value) || !Array.isArray(value.stops) || value.stops.length === 0) {
    return undefined;
  }
  const affected = parseAffectedStop(value.affected);
  const route = parseRouteStops(value);
  return affected && route ? { affected, ...route } : undefined;
};

export const parseStopTarget = (value: unknown): ChatRouteStopTarget | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const entityType = asStopEntityType(value.entityType);
  return entityType && typeof value.displayName === "string" && typeof value.entityId === "number"
    ? { displayName: value.displayName, entityId: value.entityId, entityType }
    : undefined;
};

const parseManageRouteStopsAction = (
  raw: Record<string, unknown>,
  warnings: string[]
): ChatManageRouteStopsAction | undefined => {
  const operation = OPERATIONS.find((candidate) => candidate === raw.operation);
  const entityType = asStopEntityType(raw.entityType);
  if (
    !operation ||
    !entityType ||
    typeof raw.displayName !== "string" ||
    typeof raw.entityId !== "number" ||
    typeof raw.routeId !== "number" ||
    typeof raw.routeName !== "string"
  ) {
    return undefined;
  }
  // The platform never stores a replace without a well-formed outgoing record, so a missing one is
  // not a state to render around — the whole proposal degrades to the unsupported card instead.
  const replacedStop = parseStopTarget(raw.replacedStop);
  if (operation === "replace" && !replacedStop) {
    return undefined;
  }
  const routeCard = parseRouteCardSnapshot(raw.routeCard);
  return {
    displayName: raw.displayName,
    entityId: raw.entityId,
    entityType,
    operation,
    ...(raw.position === undefined ? {} : { position: asNullableNumber(raw.position) }),
    ...(replacedStop ? { replacedStop } : {}),
    ...(routeCard ? { routeCard } : {}),
    routeId: raw.routeId,
    routeName: raw.routeName,
    ...parseSkippedRouteStops(raw.skippedStops, warnings),
    type: "manage_route_stops",
  };
};

export default parseManageRouteStopsAction;
